import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import formatRFC3339 from 'date-fns/formatRFC3339';
import fromUnixTime from 'date-fns/fromUnixTime';

import every from 'lodash/every';

import { Tooltip } from '@primer/react';

import CardHeader from '../common/Labels/CardHeader';
import Label from '../common/Labels/Label';
import TimeRangePicker from '../TimeRangePicker/TimeRangePicker';

import {
  autoGenerateBucketsFromOutputs,
  calculateBinWidthFromOutputs,
  getSortedCountsFromAutoBuckets,
} from './assay-creation-utils';
import {
  getBaselineSamplesFromResponse,
  getRoundedRangeLabel,
} from '../../utils/assay_utils';
import loadAllBaselineLogs from './loadAllBaselineLogs';

import useClientContext from '../contexts/ClientData/ClientData';
import usePipelineData from '../pipelines/PipelineDataContext';
import usePreferredTimeRange from '../contexts/TimeRangeContext/TimeRangeContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

import { useAssayLoadingContext } from './AssayLoadingContext';

import { D3BarChart } from '../charts/d3-charts/bar/D3BarChart';
import { useRefDimensions } from '../charts/d3-charts/d3-helpers';

import FileInput from '../common/FileInput/FileInput';
import RadioGroup from '../common/RadioGroup/RadioGroup';

import { ChartProvider } from '../charts/ChartContext';
import ChartWrapper from '../charts/d3-charts/ChartWrapper';

import { formatSingleLineChart } from '../charts/helpers/dataset.helpers';

const isNumericArray = (data = []) => every(data, val => {
  return val === null || !Number.isNaN(parseFloat(val))
})

const getBaselineChartOptions = iopath => ({
  scales: {
    x: {
      display: true,
      barPercentage: 1.3,
      ticks: {
        autoSkip: false,
        // max: 4,
      },
      title: {
        display: true,
        text: iopath.split(' ').join('_')
      }
    },
    y: {
      ticks: {
        beginAtZero: true,
        callback: val => Math.round(val)
      },
      title: {
        display: true,
        text: 'Count',
      }
    }
  }
})

const BaselineSelectionRadioGroup = ({
  selectedItemId,
  onSelect,
  selectedTimeRange,
  onTimeRangeChange,
  onFileOpened,
  error,
  hasRequiredFieldsForBaseline,
}) => {
  const items = [
    {
      id: 'date-range',
    },
    {
      id: 'upload-file',
    }
  ]

  const handleSelection = (id) => {
    if (onSelect) {
      onSelect(id);
    }
  }

  const renderItem = ({
    id,
  }) => {
    if (id === 'date-range') {
      return (
        <div className="flex flex-col">
          <Label className="mb-2" size="sm">
            We&#39;ve selected a baseline (based on historical data) for your assay but you can make adjustments if necessary.
          </Label>
          <TimeRangePicker
            disabledButtonClass="text-md text-brand w-min"
            selectedRange={selectedTimeRange}
            defaultRange={selectedTimeRange}
            onChange={onTimeRangeChange}
            disabled={selectedItemId !== 'date-range' || !hasRequiredFieldsForBaseline}
          />
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        <Label className="mb-2" size="sm">
          Or, upload a file using wallaroo baseline import template to create a baseline. (.txt or .csv format)
        </Label>
        <FileInput
          onFileOpened={onFileOpened}
          disabled={selectedItemId !== 'upload-file' || !hasRequiredFieldsForBaseline}
          error={selectedItemId === 'upload-file' ? error : null}
        />
      </div>
    )
  }

  if (!hasRequiredFieldsForBaseline) {
    return (
      <Tooltip className="mt-2" aria-label="Please select a named field & index to monitor">
        <RadioGroup
          id="baseline-selection-choice"
          selectedItemId={selectedItemId}
          items={items}
          onSelect={handleSelection}
          renderItem={renderItem}
          disabled
        />
      </Tooltip>
    )
  }

  return (
    <RadioGroup
      id="baseline-selection-choice"
      selectedItemId={selectedItemId}
      items={items}
      onSelect={handleSelection}
      renderItem={renderItem}
    />
  )
}

const DEFAULT_DATASET = { id: 'bar-chart', barColor: 'rgba(3, 55, 78, 0.7)', data: [] };

// Efficient Min/Max calculation using arr reduce -> avoids callstack overloads
const minAndMaxOf = (arr) => arr.reduce(([min, max], val) => [Math.min(min, val), Math.max(max, val)], [
  Number.POSITIVE_INFINITY,
  Number.NEGATIVE_INFINITY,
]);

export const SelectBaselineChart = ({
  assayConfig,
  setAssayConfig,
  setBaselineDataFlag,
  setExternalError,
  hasRequiredFieldsForBaseline,
  locations,
}) => {
  const {
    startTimestamp,
    endTimestamp,
    selectedTimeRange,
    onTimeRangeChange,
    binCount = 15,
  } = usePreferredTimeRange()
  const { useArrowLogFormat } = useClientContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { pipeline } = usePipelineData();
  const { assayPreviewLoading, setAssayPreviewLoading } = useAssayLoadingContext();

  const [outputs, setOutputs] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);
  const [labels, setLabels] = useState([]);
  const [error, setError] = useState('Nothing to show right now...');
  const [dataset, setDataset] = useState({ id: 'bar-chart', barColor: 'rgba(3, 55, 78, 0.7)', data: [] });
  const [selectedItemId, setSelectedItemId] = useState('date-range');
  const [fileData, setFileData] = useState(null);

  const containerRef = useRef(null);
  const dimensions = useRefDimensions(containerRef)

  const handleError = useCallback((msg) => {
    setExternalError(msg);
    setError(msg);
  }, [setExternalError])

  // Reset internal state when the radio option changes
  useEffect(() => {
    setOutputs([]);
    setDataset(DEFAULT_DATASET);
    setLabels([]);
    setDataPoints([]);
    setBaselineDataFlag(false);
  }, [selectedItemId, setBaselineDataFlag])

  // Persist selected baseline range to config
  useEffect(() => {
    const formattedStartDate = formatRFC3339(fromUnixTime(startTimestamp));
    const formattedEndDate = formatRFC3339(fromUnixTime(endTimestamp));

    setAssayConfig(conf => conf.setBaselineRange(formattedStartDate, formattedEndDate));
  }, [startTimestamp, endTimestamp, setAssayConfig])

  // Parses File data for outputs and throws an error if the format is invalid
  useEffect(() => {
    if (selectedItemId !== 'upload-file') {
      return;
    }

    setAssayPreviewLoading(true);

    if (!fileData) {
      setOutputs([]);
      setDataset(DEFAULT_DATASET);
      setLabels([]);
      setDataPoints([]);

      setAssayConfig(conf => conf.setBaselineVector([]));

      handleError('Unable to process file, please adjust accordingly');
    } else if (!fileData?.length) {
      setOutputs([]);
      setDataset(DEFAULT_DATASET);
      setLabels([]);
      setDataPoints([]);

      setAssayConfig(conf => conf.setBaselineVector([]));

      handleError('Unable to process file, please adjust accordingly');
    } else {
      const splitFileData = fileData.replaceAll(/'|"|\s/g, '').split(',');
      const cleanFileData = splitFileData?.length ? splitFileData.map(entry => entry?.length ? entry : null) : []

      if (!cleanFileData?.length || !isNumericArray(cleanFileData)) {
        setOutputs([]);
        setDataset(DEFAULT_DATASET);
        setLabels([]);
        setDataPoints([]);

        setAssayConfig(conf => conf.setBaselineVector([]));

        handleError('Unable to process file, please adjust accordingly');
      } else {
        const numericFileData = cleanFileData.map(val => {
          if (val === null) {
            return null;
          }
          return parseFloat(val)
        })

        setOutputs(numericFileData);

        setAssayConfig(conf => conf.setBaselineVector(numericFileData));
        handleError(null);
      }
    }
  }, [fileData, handleError, selectedItemId, setAssayConfig, setAssayPreviewLoading])

  // Use Selected Date Range to query for output data
  useEffect(() => {
    (async () => {
      if (selectedItemId !== 'date-range') {
        return;
      }
      const formattedStartDate = formatRFC3339(fromUnixTime(startTimestamp));
      const formattedEndDate = formatRFC3339(fromUnixTime(endTimestamp));

      handleError(null);

      try {
        setAssayPreviewLoading(true);

        const ret = await loadAllBaselineLogs(
          pipeline.pipelineID,
          currentWorkspaceId,
          formattedStartDate,
          formattedEndDate,
          locations,
        );

        if (!ret || ret.length < 1) {
          // We are explicitly not setting an error here because the chart will show 'not enough data'.
          setBaselineDataFlag(false);
          setAssayPreviewLoading(false);
          setDataPoints([]);
          return;
        }

        // Get all outputs from the result data.
        setOutputs(getBaselineSamplesFromResponse(ret, assayConfig.getIOPath(), useArrowLogFormat));
      } catch (e) {
        // Intentional error.
        // eslint-disable-next-line no-console
        console.error(e);
        handleError('Failed to load baseline.');

        setAssayPreviewLoading(false);
      }
    })();
  }, [assayConfig, useArrowLogFormat, binCount, currentWorkspaceId, endTimestamp, pipeline.pipelineID, setError, setBaselineDataFlag, startTimestamp, setAssayPreviewLoading, selectedItemId, handleError, locations]);

  // TODO - Figure out why so many bars are generated for holdout data - is this expected?
  // Generate a chart of historical results to use as a baseline (file data based).
  useEffect(() => {
    if (outputs?.length) {
      const minAndMax = minAndMaxOf(outputs);
      // Calculate max/min from the data set.
      const outputsMin = minAndMax[0];
      // TODO: Collapse into one reduce for efficiency.
      const outputsMax = minAndMax[1];

      // Calculate Bin Width based on the Outputs
      const autoBinWidth = calculateBinWidthFromOutputs(outputs, outputsMin, outputsMax);

      // Generate Buckets based on Outputs and Bin Width
      const autoBuckets = autoGenerateBucketsFromOutputs(outputs, autoBinWidth, outputsMin, outputsMax);

      setLabels(autoBuckets.map(x => getRoundedRangeLabel(x, x + autoBinWidth)));

      // Generate Sorted Counts from Autobuckets
      const sortedCounts = getSortedCountsFromAutoBuckets(outputs, autoBuckets)
      setDataPoints(sortedCounts);

      // The number of data points required to run an assay
      if (outputs.length < 100) {
        handleError('Not enough data is available for an assay.');
        setBaselineDataFlag(false);
      } else {
        setBaselineDataFlag(true);
      }
    } else {
      setBaselineDataFlag(false);
    }

    setAssayPreviewLoading(false);
  }, [handleError, outputs, setAssayPreviewLoading, setBaselineDataFlag])

  // Combined dataPoints and labels to create data([{x: val, y: val}, {x: val, y: val}, ...]) for dataset
  useEffect(() => {
    if (dataPoints.length > 0) {
      const combineXandY = dataPoints.map((dataPoint, i) => ({ x: labels[i], y: dataPoint }))
      const update = { ...dataset, data: combineXandY }
      setDataset(update)
    }
  // The eslint line is to get around adding dataset to the dependencies, which when added updates infinitely
  // eslint-disable-next-line
  }, [dataPoints, labels])

  return <>
    <div className="flex flex-col justify-start items-start mb-4">
      <CardHeader>Specify Baseline</CardHeader>
      <BaselineSelectionRadioGroup
        selectedItemId={selectedItemId}
        onSelect={setSelectedItemId}
        selectedTimeRange={selectedTimeRange}
        onTimeRangeChange={onTimeRangeChange}
        onFileOpened={setFileData}
        error={error}
        hasRequiredFieldsForBaseline={hasRequiredFieldsForBaseline}
      />
    </div>
    <ChartProvider
      datasets={[dataset]}
      chartID="select-baseline"
      dropdownOptions={[]}
      prepareDataset={formatSingleLineChart}
    >
      <ChartWrapper
        className="m-1"
        containerRef={containerRef}
        chartTitle="Baseline"
        loadingText="Loading baseline data..."
        isLoading={assayPreviewLoading}
        message={error}
        hideDropdownMenu
      >
        <D3BarChart
          chartId="select-baseline"
          dataset={[dataset]}
          xAxisLabel={getBaselineChartOptions(assayConfig.getIOPath()).scales.x.title.text}
          yAxisLabel={getBaselineChartOptions(assayConfig.getIOPath()).scales.y.title.text}
          containerWidth={dimensions?.width ? dimensions.width : 0}
          margins={{
            top: 20,
            right: 20,
            bottom: 100,
            left: 64
          }}
        />
      </ChartWrapper>
    </ChartProvider>
  </>
}

export default SelectBaselineChart;
